import { useEffect, useMemo, useState } from "react";
import { db } from "../lib/db";
import { fetchCommons } from "../lib/api";
import { STIMULI } from "../lib/stimuli";
import { CommonsSummary, LocalSession } from "../types";
import { HeatCanvas } from "./HeatCanvas";
import { useAppStore } from "../stores/useAppStore";

export function StatsPage() {
  const { roomId, commons, setCommons } = useAppStore();
  const [sessions, setSessions] = useState<LocalSession[]>([]);
  const [summary, setSummary] = useState<CommonsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    db.sessions.orderBy("createdAt").reverse().toArray().then(setSessions).catch(() => setSessions([]));
    fetchCommons(roomId)
      .then((next) => {
        setSummary(next);
        setCommons(next.cells);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [roomId, setCommons]);

  const localTop = useMemo(() => {
    const totals = new Map<number, number>();
    for (const session of sessions) {
      for (const cell of session.metrics.heat) {
        totals.set(cell.i, (totals.get(cell.i) || 0) + cell.dwellMs + cell.touches * 1200 + cell.visits * 250);
      }
    }
    return Array.from(totals.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [sessions]);

  const commonsTop = useMemo(() => [...commons]
    .sort((a, b) => b.dwellMs + b.touches * 1200 + b.visits * 250 - (a.dwellMs + a.touches * 1200 + a.visits * 250))
    .slice(0, 8), [commons]);

  return (
    <section className="page-grid stats-page">
      <div className="panel hero-panel">
        <p className="eyebrow">Commons oracle</p>
        <h1>Attention weather</h1>
        <p>Everyone’s anonymized touch/dwell traces accumulate into a shared map of what claims people and what quietly escapes them.</p>
        {error && <p className="error">{error}</p>}
      </div>
      <div className="panel heat-panel">
        <HeatCanvas commons={commons} blend={true} label="aggregate commons heatmap" />
      </div>
      <div className="panel">
        <h2>Local archive</h2>
        <dl className="metric-grid compact">
          <div><dt>runs</dt><dd>{sessions.length}</dd></div>
          <div><dt>last profile</dt><dd>{sessions[0]?.oracle.name || "—"}</dd></div>
          <div><dt>avg coverage</dt><dd>{sessions.length ? Math.round(sessions.reduce((s, x) => s + x.metrics.coverage, 0) / sessions.length * 100) : 0}%</dd></div>
          <div><dt>room</dt><dd>{roomId}</dd></div>
        </dl>
        <ol className="rank-list">
          {localTop.map(([index, score]) => <li key={index}><span>{STIMULI[index].title}</span><b>{Math.round(score)}</b></li>)}
        </ol>
      </div>
      <div className="panel">
        <h2>Commons hot tiles</h2>
        <p className="small muted">From D1 aggregate + live Durable Object patches. {summary ? `${summary.sessions} saved sessions.` : "No global summary yet."}</p>
        <ol className="rank-list">
          {commonsTop.map((cell) => <li key={cell.i}><span>{STIMULI[cell.i].title}</span><b>{Math.round(cell.dwellMs / 1000)}s</b></li>)}
        </ol>
      </div>
    </section>
  );
}
